/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useMemo,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import KeyboardArrowLeftTwoTone from '@material-ui/icons/KeyboardArrowLeftTwoTone';
import classnames from 'classnames';
import {Polygon} from 'esri/geometry';
import {translate} from '@ion/core/src/locale/locale';
import {FindNearbyGeofence} from '@ion/core/src/types/entities';
import {isTrue} from '@ion/core/src/utils/BooleanUtils';
import {hexToRgb} from '@ion/core/src/utils/StringUtil';
import {useIsDesktop, useMedia} from '@ion/dls/src/hooks/useMedia';
import Render from '@ion/dls/src/render/Render';
import {
  dlsAlt,
  dlsColor,
  dlsShadow
} from '@ion/dls/src/theme/theme';
import CloseButton from '../../components/buttons/CloseButton';
import {IconButton} from '../../components/buttons/IconButton';
import Tooltip from '../../components/tooltip/Tooltip';
import SystemGeofenceIcon from '../../icons/current/SystemGeofenceIcon';
import {MapEditIcon} from '../../icons/MapEditIcon';
import {PopupCloseIcon} from '../../icons/PopupCloseIcon';
import {SystemFindNearestIcon} from '../../icons/SystemFindNearestIcon';
import {toggleThemeStyle} from '../../theme/helpers';
import {dlsLayout} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import {
  BodyTypographyVariantEnum,
  IconSize
} from '../../types/types';
import {BodyTypography} from '../../typography/BodyTypography';
import {LabelTypography} from '../../typography/LabelTypography';
import {mapViewGoToOptions} from '../constants';
import {
  GENERATE_EVENTS_VALUE,
  NONE_TRIGGER_EVENT
} from '../geofences/constants';
import {EXTENT_FACTOR, FindNearbyType} from '../layers/AssetLayer';
import FindNearby from './findnearby/FindNearby';
import GeofenceDetailedActionContainerPopup from './GeofenceDetailedActionContainerPopup';

export interface GeofenceDetailedViewPopupType {
  mapView?: __esri.MapView;
  setHideGraphicsForEdit?: Function;
  setEditGeofenceGraphic?: Function;
  canEditGeofence?: boolean;
  isGeofenceAccess?: boolean,
  ancestorAccountIds?: number[];
  geofeceFeature: __esri.Feature;
  isFindNearbyPopup?: boolean,
  setFindNearbyPopupForGeofence?: Function;
  onFindNearyBy?: Function,
  onFindNearbyPopupStatusChange?: Function,
  onFindButtonStateChange?: Function,
  onFindNearbyBreadcrumbBackClick?: Function,
  findLabel?: FindNearbyType,
  isShowEdit?: boolean;
  excludeFindNearby?: boolean;
  isTrackingV2?: boolean;
}

const {label}       = translate('tracking:breadcrumb.findNearby');
const maxCharacters = 50;

interface StyleProps {
  showFindNearBy: boolean;
};

const useStyles = makeStyles((theme) => ({
  toolPopup : {
    margin : dlsLayout('margins')
  },
  value : {
    textAlign : 'right !important' as any
  },
  button : {
    minWidth   : 0,
    marginLeft : 'auto'
  },
  findNearby : {
    minWidth : 0,
    padding  : '6px 3px'
  },
  contentContainer : {
    width : toggleThemeStyle('87%', '100%')
  },
  statusContainer : toggleThemeStyle({
    display        : 'flex',
    flexDirection  : 'row',
    justifyContent : 'space-between',
    alignItems     : 'center',
    height         : 26,
    paddingRight   : 5,
    paddingLeft    : 8
  }, {
    // the popup's own width, its padding inside it - a fixed 318px ran past
    // the 295px map popup, which clips it, cutting off the close button ...
    boxSizing       : 'border-box',
    width           : '100%',
    height          : '56px',
    display         : 'flex',
    alignItems      : 'center',
    paddingLeft     : '16px',
    paddingRight    : '16px',
    flexDirection   : 'row',
    justifyContent  : 'space-between',
    backgroundColor : dlsColor('grey6'),
    borderRadius    : '8px 8px 0px 0px'
  }),
  iconContent : {
    height     : 'inherit',
    display    : 'flex',
    alignItems : 'center',
    gap        : 3,
    color      : dlsColor('white')
  },
  geofenceIcon : {
    height          : '10px !important',
    width           : 10,
    border          : `1px solid ${dlsColor('aaBlue')}`,
    backgroundColor : dlsColor('aaBlue')
  },
  fontContent : toggleThemeStyle({
    fontWeight : 700,
    fontSize   : 12,
    lineHeight : '16px'
  }, {
    color         : dlsColor('grey1'),
    fontSize      : '16px',
    fontFamily    : 'Inter',
    fontWeight    : 600,
    lineHeight    : '20px',
    letterSpacing : '0'
  }),
  uppercase : {
    textTransform : toggleThemeStyle('uppercase', 'capitalize'),
    paddingLeft   : 4
  },
  detailContainer : toggleThemeStyle({
    maxHeight : 130,
    minHeight : '115px !important'
  }, {
    gap           : '8px',
    display       : 'flex',
    padding       : '16px',
    flexDirection : 'column'
  }),
  container : toggleThemeStyle({
    margin        : '0',
    cursor        : 'pointer',
    overflow      : 'hidden',
    display       : 'flex',
    flexDirection : 'row'
  }, {
    // as wide as the map popup it sits in ...
    width           : '100%',
    cursor          : 'pointer',
    display         : 'flex',
    overflow        : 'hidden',
    boxShadow       : dlsShadow(1),
    borderRadius    : '8px',
    flexDirection   : 'row',
    backgroundColor : dlsColor('white')
  }),
  deviceNameContainer : toggleThemeStyle({
    color         : dlsColor('aaBlue'),
    textTransform : 'uppercase',
    lineHeight    : '24px !important',
    marginLeft    : '10px',
    padding       : '0 4px !important',
    height        : 20
  }, {
    color         : dlsColor('lightTeal'),
    height        : '20px',
    padding       : '0 4px !important',
    fontSize      : '14px',
    fontFamily    : 'Inter',
    fontWeight    : 600,
    lineHeight    : '16px',
    marginLeft    : '10px',
    letterSpacing : '0',
    textTransform : 'capitalize'
  }),
  subText : {
    color         : dlsColor('grey3'),
    margin        : '0 !important',
    padding       : '0 !important',
    fontSize      : '10px !important',
    fontWeight    : 400,
    textTransform : 'uppercase',
    marginLeft    : '13px !important'
  },
  popupContentAttributeName : toggleThemeStyle({
    width   : 80,
    display : 'inline-block'
  }, {
    color         : dlsColor('grey3'),
    width         : '80px',
    display       : 'inline-block',
    fontSize      : '12px',
    fontFamily    : 'Inter',
    fontWeight    : 500,
    lineHeight    : '16px',
    letterSpacing : '0',
    textTransform : 'capitalize'
  }),
  popupAttributeName : toggleThemeStyle({}, {
    color         : dlsColor('grey2'),
    fontSize      : '12px',
    fontFamily    : 'Inter',
    fontWeight    : 600,
    lineHeight    : '16px',
    letterSpacing : '0',
    textTransform : 'capitalize'
  }),
  actionContainer : {
    width           : '13%',
    backgroundColor : dlsColor('grey6')
  },
  ctaContainer : {
    display       : 'flex',
    flexDirection : 'column',
    height        : '100%'
  },
  ctaActionContainer : {
    display        : 'flex',
    flexDirection  : 'column',
    height         : ({showFindNearBy}: StyleProps) => showFindNearBy ? '87%' : '100%',
    justifyContent : 'space-evenly',
    gap            : 4
  },
  CTAlabel : {
    height : '22px !important',
    width  : '22px !important'
  },
  popupContainer : {
    display : 'flex'
  }
}));

const iconsTooltip = makeStyles((theme) => ({
  arrow : {
    color : dlsColor('grey1')
  },
  tooltip : {
    ...dlsAlt('components', 'tooltip')
  }
}));

function IconCustomTooltip(props) {
  return <Tooltip arrow classes={iconsTooltip()} {...props} placement='bottom' />;
}

const Description = function({description, classes}: {description: string, classes: any}) {
  const {isPhone} = useMedia();
  if (description?.length > maxCharacters && !isPhone) {
    return (
      <IconCustomTooltip title={<span>{description}</span>}>
        <span className={classes.popupAttributeName}>{description.substring(0, maxCharacters).concat('...')}</span>
      </IconCustomTooltip>
    );
  }
  return <span className={classes.popupAttributeName}>{description}</span>;
};

const GeofenceDetailedViewPopup = ({
  mapView,
  isGeofenceAccess,
  geofeceFeature,
  setHideGraphicsForEdit,
  setEditGeofenceGraphic,
  canEditGeofence,
  isFindNearbyPopup,
  onFindNearyBy,
  onFindNearbyPopupStatusChange,
  onFindButtonStateChange,
  onFindNearbyBreadcrumbBackClick,
  findLabel,
  ancestorAccountIds,
  isShowEdit = false,
  setFindNearbyPopupForGeofence,
  excludeFindNearby = false,
  isTrackingV2 = false
}: GeofenceDetailedViewPopupType) => {
  const classes                             = useStyles();
  const [showFindNearBy, setShowFindNearBy] = useState(false);
  const [showToolTip, setShowToolTip]       = useState(false);
  const {attributes}                        = geofeceFeature?.graphic;
  const isDesktop                           = useIsDesktop();
  const {isLegacy: isLegacyTheme}           = useThemeContext();

  const hasEditGeofencePermission = useMemo(() => (
    !ancestorAccountIds?.includes(attributes?.landmarkAccountId)
  ), [ancestorAccountIds, attributes?.landmarkAccountId]);

  /**
   * Defines Landmark Triggers options
   */
  const LANDMARK_TRIGGER_OPTIONS = [{
    value : NONE_TRIGGER_EVENT,
    label : translate('maps:popup.triggerNone')
  }, {
    value : GENERATE_EVENTS_VALUE,
    label : translate('maps:popup.triggerEnterexit')
  }];

  /**
   * @private
   */
  function onEditGeofences() {
    const {geometry} = geofeceFeature?.graphic;
    if (geometry) {
      mapView.goTo(geometry?.extent?.expand(EXTENT_FACTOR), mapViewGoToOptions)
        .then(function() {
          mapView.popup?.close?.();
          setShowToolTip(false);
          setHideGraphicsForEdit?.(geofeceFeature?.graphic);
          setEditGeofenceGraphic?.(geofeceFeature.graphic);
        });
    }
  }

  const onMouseHover = useCallback(() => {
    setShowToolTip(!showToolTip);
  }, [showToolTip]);

  function getTriggerLabel(geofenceType): string {
    const triggerOption = LANDMARK_TRIGGER_OPTIONS.find(({value}) => value === geofenceType);
    return triggerOption ? triggerOption?.label : '';
  }

  function onFindNearby() {
    const {geometry} = geofeceFeature?.graphic;
    if (geometry) {
      const {type, centroid} = geometry as Polygon;
      if (type === 'polygon') {
        const geofenceCoordinates: FindNearbyGeofence = {
          latitude  : centroid.latitude,
          longitude : centroid.longitude
        };
        setShowFindNearBy(false);
        mapView.goTo(geometry?.extent?.expand(EXTENT_FACTOR), mapViewGoToOptions)
          .then(function() {
            setFindNearbyPopupForGeofence?.(geofenceCoordinates, label);
          });
      }
    }
  }

  const showFindNearbyContent = useCallback(function() {
    if (!isTrackingV2) {
      setShowFindNearBy(true);
    } else {
      setFindNearbyPopupForGeofence?.(geofeceFeature?.graphic);
    }
  }, [isTrackingV2]);

  const hideFindNearbyContent = useCallback(function() {
    setShowFindNearBy(false);
  }, []);

  const onClose = function() {
    mapView?.popup?.close?.();
  };

  const isRestrictedLabel = useMemo(() =>
    translate(`maps:popup.restricted${isTrue(attributes?.landmarkRestricted) ? 'True' : 'False'}`) ?? translate('common:notAvailable'),
  [attributes]);

  const landmarkLabel = useMemo(
    () => attributes?.landmarkAccount ?? translate('common:notAvailable'),
    [attributes]);

  return (
    <>
      <div className={classes.container}>
        {!showFindNearBy && (
          <div className={classes.contentContainer}>
            <div className={classes.statusContainer} style={{
              ...(isLegacyTheme && {
                backgroundColor : `rgba(${hexToRgb(dlsColor('grey3'))}, 0.6)`
              })
            }}>
              <div className={classnames(classes.iconContent)}>
                <Render>
                  <Render.If condition={isLegacyTheme}>
                    <span className={classes.geofenceIcon} />
                  </Render.If>
                  <Render.Else>
                    <SystemGeofenceIcon size={IconSize.medium} fill={dlsColor('grey1')} />
                  </Render.Else>
                </Render>
                <span className={classnames(classes.fontContent, classes.uppercase)}>{translate('common:entityLabels.geofence')}</span>
              </div>
              <Render>
                <Render.If condition={isLegacyTheme}>
                  <div className={classnames(classes.iconContent, classes.fontContent)}>
                    <IconButton disableHover
                      onClick={onClose}
                      icon={<PopupCloseIcon size={10} fill={dlsColor('white')}/>} />
                  </div>
                </Render.If>
                <Render.Else>
                  <div className={classes.popupContainer}>
                    <GeofenceDetailedActionContainerPopup hideFindNearbyContent={hideFindNearbyContent}
                      showFindNearbyContent={showFindNearbyContent}
                      isTrackingV2={isTrackingV2}
                      showFindNearBy={showFindNearBy}
                      excludeFindNearby={excludeFindNearby} />
                    <CloseButton onClick={onClose} />
                  </div>
                </Render.Else>
              </Render>
            </div>
            <div className={classes.detailContainer}>
              <BodyTypography variant={BodyTypographyVariantEnum.body2}
                id={'geofence-popup-landmark'}
                className={classnames(classes.deviceNameContainer, classes.fontContent)}>
                {attributes?.landmarkName?.trim?.()}
              </BodyTypography>
              <LabelTypography variantType='label2'
                className={classes.subText}
                id={'geofence-popup-category'}>
                <span className={classes.popupContentAttributeName}>{translate('tracking:filters.assetCategories')}</span>
                <span className={classes.popupAttributeName}>{attributes?.landmarkCategory ?? translate('common:notAvailable')}</span>
              </LabelTypography>
              <LabelTypography variantType='label2'
                className={classes.subText}
                id={'geofence-popup-groups'}>
                <span className={classes.popupContentAttributeName}>{translate('tracking:filters.groups')}</span>
                <span className={classes.popupAttributeName}>{landmarkLabel}</span>
              </LabelTypography>
              <LabelTypography variantType='label2'
                className={classes.subText}
                id={'geofence-popup-triggers'}>
                <span className={classes.popupContentAttributeName}>{translate('maps:popup.trigger')}</span>
                <span className={classes.popupAttributeName}>{getTriggerLabel(attributes?.geofenceType) ?? translate('common:notAvailable')}</span>
              </LabelTypography>
              <LabelTypography variantType='label2'
                className={classes.subText}
                id={'geofence-popup-isrestricted'}>
                <span className={classes.popupContentAttributeName}>{translate('maps:popup.restricted')}</span>
                <span className={classes.popupAttributeName}>{isRestrictedLabel}</span>
              </LabelTypography>
              <LabelTypography variantType='label2'
                className={classes.subText}
                id={'geofence-popup-isrestricted'}>
                <span className={classes.popupContentAttributeName}>{translate('maps:popup.description')}</span>
                <Description description={attributes?.description || translate('common:notAvailable')}
                  classes={classes} />
              </LabelTypography>
            </div>
          </div>)}
        {showFindNearBy && !isTrackingV2 && (
          <div className={classes.contentContainer}>
            <FindNearby onFindNearyBy={onFindNearyBy}
              onFindNearbyPopupStatusChange={onFindNearbyPopupStatusChange}
              onFindButtonStateChange={onFindButtonStateChange}
              onFindButtonClick={onFindNearby}
              showFindNearByPopup={isFindNearbyPopup}
              assetData={geofeceFeature?.graphic}
              geofenceCoordinates={findLabel.geofence}
              locationCoordinates={findLabel.location}
              typeToFindBy={findLabel.type} />
          </div>
        )}
        <Render.If condition={isLegacyTheme}>
          <div className={classes.actionContainer}>
            <div className={classes.ctaContainer}>
              <div className={classes.ctaActionContainer}>
                {!excludeFindNearby && (
                  <Tooltip id='findNearbyTooltip' title={translate('tracking:buttons.findNearby')}
                    renderOnlyChildren={!isDesktop}
                    arrow>
                    <IconButton icon={<SystemFindNearestIcon />}
                      color={dlsColor('caBlue')}
                      size={'small'}
                      labelClass={classes.CTAlabel}
                      onClick={showFindNearbyContent} />
                  </Tooltip>)}
                <Render.If condition={isGeofenceAccess}>
                  <Tooltip title={translate('maps:controls.edit')} arrow open={showToolTip}>
                    <IconButton icon={<MapEditIcon />}
                      color={dlsColor('caBlue')}
                      disabled={!hasEditGeofencePermission}
                      size={'small'}
                      onMouseEnter={onMouseHover}
                      onMouseLeave={onMouseHover}
                      labelClass={classes.CTAlabel}
                      onClick={onEditGeofences} />
                  </Tooltip>
                </Render.If>
              </div>
              {showFindNearBy && !isTrackingV2 && (
                <IconButton icon={<KeyboardArrowLeftTwoTone />}
                  color={dlsColor('caBlue')}
                  padding={'4px'}
                  onClick={hideFindNearbyContent} />
              )}
            </div>
          </div>
        </Render.If>
      </div>
      <style>{`
      .esri-popup__footer {
        display: none;
      }
    `}</style>
    </>
  );
};

export default GeofenceDetailedViewPopup;
